import { useEditor } from "@grapesjs/react";
import {
  Fullscreen,
  GridOn,
  Preview,
  Redo,
  Undo,
} from "@mui/icons-material";
import { alpha, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useLightningLanderContext } from "../providers/LightningLanderProvider";
import { customJsScript } from "../constants";
import { getCanvasDocument } from "../utils";
import LanderPreviewDialog from "./LanderPreviewDialog";

interface CommandButton {
  id: string;
  iconPath?: React.ReactNode;
  options?: Record<string, any>;
  disabled?: () => boolean;
}

function LightningLandersTopButtons() {
  const editor = useEditor();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { component } = useLightningLanderContext();
  const [showPreview, setShowPreview] = useState(false);
  const [, setUpdateCounter] = useState(0);
  const { UndoManager, Commands } = editor;
  const cmdButtons: CommandButton[] = [
    {
      id: "core:component-outline",
      iconPath: <GridOn />,
    },
    {
      id: "core:fullscreen",
      iconPath: <Fullscreen />,
      options: { target: "#root" },
    },

    {
      id: "core:undo",
      iconPath: <Undo />,
      disabled: () => !UndoManager.hasUndo(),
    },
    {
      id: "core:redo",
      iconPath: <Redo />,
      disabled: () => !UndoManager.hasRedo(),
    },
  ];

  const getPreviewComponent = useCallback(() => {
    if (!component || !editor) return component;

    // Get current editor state
    const html = editor.getHtml();
    const css = editor.getCss({
      avoidProtected: true,
      keepUnusedStyles: true,
    }) || "";

    // Get JS from canvas
    const canvasDoc = getCanvasDocument(editor);
    let js = component.js || "";
    if (canvasDoc) {
      const existingScript = canvasDoc.querySelector(
        `script[${customJsScript}]`
      );
      if (existingScript) {
        js = existingScript.textContent || "";
      }
    }

    return {
      ...component,
      html,
      css,
      js,
    };
  }, [component, editor]);

  const handlePreview = useCallback(() => {
    setShowPreview(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
  }, []);

  useEffect(() => {
    const cmdEvent = "run stop";
    const updateEvent = "update";
    const updateCounter = () => setUpdateCounter((value) => value + 1);
    const onCommand = (id: string) => {
      cmdButtons.find((btn) => btn.id === id) && updateCounter();
    };
    editor.on(cmdEvent, onCommand);
    editor.on(updateEvent, updateCounter);

    return () => {
      editor.off(cmdEvent, onCommand);
      editor.off(updateEvent, updateCounter);
    };
  }, [editor]);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        {cmdButtons.map(({ id, iconPath, disabled, options = {} }) => (
          <IconButton
            key={id}
            onClick={() =>
              Commands.isActive(id)
                ? Commands.stop(id)
                : Commands.run(id, options)
            }
            disabled={disabled?.()}
            sx={{
              border: `1px solid ${Commands.isActive(id)
                ? alpha(theme.palette.primary.main, 0.7)
                : "transparent"
                }`,
            }}
            color={Commands.isActive(id) ? "primary" : "secondary"}
          >
            {iconPath}
          </IconButton>
        ))}
        <IconButton
          onClick={handlePreview}
          sx={{
            border: `1px solid ${showPreview
              ? alpha(theme.palette.primary.main, 0.7)
              : "transparent"
              }`,
          }}
          color={showPreview ? "primary" : "secondary"}
          title="Preview"
        >
          <Preview />
        </IconButton>
      </div>
      {showPreview && component && (
        <LanderPreviewDialog
          open={showPreview}
          onClose={handleClosePreview}
          component={getPreviewComponent()}
          title={`${component?.name || "Preview"} - Preview`}
          fullScreen={fullScreen}
        />
      )}
    </>
  );
}

export default LightningLandersTopButtons;
